import React from 'react';
import type { Layouts } from 'react-grid-layout';

import { AiInsightWidget } from '../AiInsightWidget';
import { BacktestTableWidget } from '../BacktestTableWidget';
import { QuantArcEquityWidget } from '../QuantArcEquityWidget';
import { QuantArcExecutionWidget } from '../QuantArcExecutionWidget';
import { QuantArcPositionWidget } from '../QuantArcPositionWidget';
import { WidgetWrapper } from '../WidgetWrapper';
import type { EvaluatePanelPropsBase, ResultStrategy, WidgetDefinition } from './ResultStrategy';

function formatNumber(value: number | null | undefined, digits = 2): string {
    if (value == null || !Number.isFinite(value)) return '—';
    return value.toFixed(digits);
}

function formatSigned(value: number | null | undefined, digits = 2): string {
    if (value == null || !Number.isFinite(value)) return '—';
    const prefix = value > 0 ? '+' : '';
    return `${prefix}${value.toFixed(digits)}`;
}

export class BacktestStrategy implements ResultStrategy {
    id = 'backtest';

    match(nature?: string): boolean {
        return nature === 'backtest';
    }

    getAvailableWidgets(hasAiReport: boolean): WidgetDefinition[] {
        const widgets: WidgetDefinition[] = [
            { id: 'summary', label: '📊 Backtest Summary', icon: '📊' },
            { id: 'equity', label: '📈 Performance', icon: '📈' },
            { id: 'execution', label: '🎯 Execution & Position', icon: '🎯' },
            { id: 'table', label: '📋 Decision Log', icon: '📋' },
        ];
        if (hasAiReport) {
            widgets.push({ id: 'ai-insights', label: '🤖 AI Insights', icon: '🤖' });
        }
        return widgets;
    }

    getDefaultWidgets(): string[] {
        return ['summary', 'equity', 'execution'];
    }

    getLayout(activeWidgets: string[]): Layouts {
        return {
            lg: [
                { i: 'summary', x: 0, y: 0, w: 12, h: 3 },
                { i: 'equity', x: 0, y: 3, w: 6, h: 6 },
                { i: 'execution', x: 6, y: 3, w: 6, h: 6 },
                { i: 'ai-insights', x: 0, y: 9, w: 12, h: 5 },
                { i: 'table', x: 0, y: 14, w: 12, h: 5 },
            ].filter((item) => activeWidgets.includes(item.i)),
            md: [
                { i: 'summary', x: 0, y: 0, w: 12, h: 4 },
                { i: 'equity', x: 0, y: 4, w: 12, h: 6 },
                { i: 'execution', x: 0, y: 10, w: 12, h: 6 },
                { i: 'ai-insights', x: 0, y: 16, w: 12, h: 6 },
                { i: 'table', x: 0, y: 22, w: 12, h: 5 },
            ].filter((item) => activeWidgets.includes(item.i)),
            sm: [
                { i: 'summary', x: 0, y: 0, w: 12, h: 4 },
                { i: 'equity', x: 0, y: 4, w: 12, h: 5 },
                { i: 'execution', x: 0, y: 9, w: 12, h: 5 },
                { i: 'ai-insights', x: 0, y: 14, w: 12, h: 6 },
                { i: 'table', x: 0, y: 20, w: 12, h: 5 },
            ].filter((item) => activeWidgets.includes(item.i)),
            xs: [
                { i: 'summary', x: 0, y: 0, w: 12, h: 4 },
                { i: 'equity', x: 0, y: 4, w: 12, h: 5 },
                { i: 'execution', x: 0, y: 9, w: 12, h: 5 },
                { i: 'ai-insights', x: 0, y: 14, w: 12, h: 6 },
                { i: 'table', x: 0, y: 20, w: 12, h: 5 },
            ].filter((item) => activeWidgets.includes(item.i)),
            xxs: [
                { i: 'summary', x: 0, y: 0, w: 12, h: 4 },
                { i: 'equity', x: 0, y: 4, w: 12, h: 5 },
                { i: 'execution', x: 0, y: 9, w: 12, h: 5 },
                { i: 'ai-insights', x: 0, y: 14, w: 12, h: 6 },
                { i: 'table', x: 0, y: 20, w: 12, h: 5 },
            ].filter((item) => activeWidgets.includes(item.i)),
        };
    }

    getStats(_data: any, backendStats?: Record<string, number | null> | null): React.ReactNode {
        if (!backendStats) return null;

        const statItems = [
            { label: 'Total PnL', value: backendStats.total_pnl, isPnL: true },
            { label: 'Realized PnL', value: backendStats.realized_pnl, isPnL: true },
            { label: 'Unrealized PnL', value: backendStats.unrealized_pnl, isPnL: true },
            { label: 'Trades', value: backendStats.trade_count, digits: 0 },
            { label: 'Entries', value: backendStats.entry_count, digits: 0 },
            { label: 'Exits', value: backendStats.exit_count, digits: 0 },
            { label: 'Final Position', value: backendStats.final_position, digits: 0 },
        ];

        return (
            <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', 
                gap: '0.75rem',
                width: '100%'
            }}>
                {statItems.map((item, idx) => {
                    const value = item.value as number | null;
                    const isPositive = value != null && value > 0;
                    const isNegative = value != null && value < 0;
                    const color = item.isPnL 
                        ? (isPositive ? 'var(--accent-success)' : isNegative ? 'var(--accent-danger)' : 'var(--text-secondary)')
                        : 'var(--text-primary)';

                    return (
                        <div key={idx} style={{
                            background: 'rgba(255, 255, 255, 0.03)',
                            border: '1px solid var(--surface-glass-border)',
                            borderRadius: '8px',
                            padding: '0.75rem',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '0.25rem',
                            justifyContent: 'center',
                            transition: 'background 0.2s ease',
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.03)'}
                        >
                            <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                {item.label}
                            </span>
                            <span style={{ fontSize: '1.25rem', fontWeight: '700', color, fontFamily: 'monospace' }}>
                                {item.isPnL ? formatSigned(value, 2) : formatNumber(value, item.digits ?? 2)}
                            </span>
                        </div>
                    );
                })}
            </div>
        );
    }

    renderWidget(widgetId: string, props: EvaluatePanelPropsBase): React.ReactNode {
        switch (widgetId) {
            case 'equity':
                return <QuantArcEquityWidget data={props.result ?? []} />;
            case 'position':
                return <QuantArcPositionWidget data={props.result ?? []} />;
            case 'summary':
                return (
                    <WidgetWrapper title="📊 QuantArc Summary">
                        {this.getStats(props.result, props.backendStats)}
                    </WidgetWrapper>
                );
            case 'execution':
                return <QuantArcExecutionWidget data={props.result ?? []} />;
            case 'table':
                return (
                    <WidgetWrapper title="📋 Decision Log">
                        <BacktestTableWidget data={props.result ?? []} />
                    </WidgetWrapper>
                );
            case 'ai-insights':
                if (!props.currentAiReport) return null;
                return <AiInsightWidget report={props.currentAiReport} />;
            default:
                return null;
        }
    }
}
